import { defineStore } from 'pinia';

import type { EmailTemplate, EmailTemplateFolder } from '@stuut/api/models';

interface State {
  folders: EmailTemplateFolder[];
  templates: EmailTemplate[];
  totalTemplates: number;
}

export const useEmailTemplates = defineStore('emailTemplates', {
  actions: {
    addTemplatesToState(templates: EmailTemplate[]) {
      this.templates.push(...templates);

      // Iterating over folders and not templates
      // seems cleaner IMO
      this.folders.forEach((folder) => {
        const templatesForFolder = templates.filter((t) => t.folder?.id === folder.id);
        folder.templates.push(...templatesForFolder);
      });
    },

    removeFoldersFromState(folders: EmailTemplateFolder[]) {
      const templatesToRemove = folders.flatMap((folder) => folder.templates);
      this.removeTemplatesFromState(templatesToRemove);
      this.folders = this.folders.filter((f) => !folders.some((folder) => folder.id === f.id));
    },

    removeTemplatesFromState(templates: EmailTemplate[]) {
      this.templates = this.templates.filter(
        (t) => !templates.some((template) => template.id === t.id),
      );

      this.folders.forEach((folder) => {
        folder.templates = folder.templates.filter(
          (t) => !templates.some((template) => template.id === t.id),
        );
      });
    },

    setNewDefaultTemplate(template: EmailTemplate) {
      this.templates.forEach((t) => {
        if (t.id === template.id) {
          t.is_default = true;
        } else {
          t.is_default = false;
        }
      });
    },

    updateFoldersInState(folders: EmailTemplateFolder[]) {
      for (const folder of folders) {
        const index = this.folders.findIndex((f) => f.id === folder.id);
        if (index !== -1) {
          this.folders[index] = folder;
          this.templates.forEach((template) => {
            if (template.folder?.id === folder.id) {
              template.folder = folder;
            }
          });
        }
      }
    },

    updateTemplatesInState(templates: EmailTemplate[]) {
      // Doesn't update the folders
      // Use removeTemplatesFromState then addTemplatesToState
      templates.forEach((updatedTemplate) => {
        const index = this.templates.findIndex((t) => t.id === updatedTemplate.id);
        if (index !== -1) {
          this.templates[index] = updatedTemplate;
        }
      });
    },
  },
  getters: {
    getById: (state) => {
      return (templateId: string) =>
        state.templates.find((template) => template.id === templateId) as EmailTemplate | undefined;
    },
  },
  state: (): State => ({
    folders: [],
    templates: [],
    totalTemplates: 0,
  }),
});

export default useEmailTemplates;
